import React, { useState } from "react";
import Modal from "react-modal";
import { CloseIcon, FolderIcon } from "./icons";
import { ListingCard, SkeletonCards, useDebounced, useFolders } from "./DiscoverPanel";
import { MAX_MOCKUPS, listingTitle } from "./listings";
import { useInfiniteSentinel, useListingFeed } from "./useListingFeed";

/**
 * My mockups: pick several owned mockups (by folder, with search) and add
 * them to the editor in one go.
 */
export default function LibraryDialog({ inEditor, mockupCount, busy, design, onClose, onAddMany, onDetails }) {
  const [folderId, setFolderId] = useState(null);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState([]); // listings, in pick order
  const query = useDebounced(search.trim());
  const folders = useFolders(true);
  const feed = useListingFeed({ tab: "mine", search: query, folderId });
  const sentinel = useInfiniteSentinel(feed.loadMore, [feed.items.length, feed.hasMore, folderId]);
  const slots = Math.max(0, MAX_MOCKUPS - mockupCount);
  const [notice, setNotice] = useState("");

  const toggle = (listing) => {
    if (inEditor.has(String(listing.id))) {
      onDetails(listing);
      return;
    }
    if (picked.some((l) => l.id === listing.id)) {
      setPicked(picked.filter((l) => l.id !== listing.id));
      setNotice("");
    } else if (picked.length >= slots) {
      setNotice(slots ? `Only ${slots} more mockup${slots === 1 ? "" : "s"} fit in the editor.` : "The editor is full. Remove a mockup to add another.");
    } else {
      setPicked([...picked, listing]);
      setNotice("");
    }
  };

  const add = async () => {
    await onAddMany(picked);
    setPicked([]);
  };

  return (
    <Modal isOpen onRequestClose={busy ? undefined : onClose} className="sw-dialog sw-library-dialog" overlayClassName="sw-dialog-overlay" contentLabel="My mockups">
      <header className="sw-library-header">
        <div>
          <h2>My mockups</h2>
          <p>Choose mockups to add to your editor</p>
        </div>
        <button type="button" className="sw-icon-button" onClick={onClose} aria-label="Close my mockups" disabled={busy}>
          <CloseIcon />
        </button>
      </header>
      <div className="sw-library-body">
        <nav className="sw-library-folders" aria-label="Mockup folders">
          <button type="button" aria-pressed={!folderId} className={!folderId ? "is-active" : ""} onClick={() => setFolderId(null)}>
            <FolderIcon />
            <span>My mockups</span>
          </button>
          {folders.map((f) => (
            <button key={f.id} type="button" aria-pressed={folderId === f.id} className={folderId === f.id ? "is-active" : ""} onClick={() => setFolderId(f.id)}>
              <FolderIcon />
              <span>{f.name}</span>
              {Array.isArray(f.listings) && <small>{f.listings.length}</small>}
            </button>
          ))}
        </nav>
        <section className="sw-library-content">
          <div className="sw-library-toolbar">
            <input type="search" placeholder="Search my mockups…" aria-label="Search my mockups" value={search} onChange={(e) => setSearch(e.target.value)} />
            <span>
              {mockupCount}/{MAX_MOCKUPS} in editor
            </span>
          </div>
          <div className="sw-library-grid">
            {feed.items.map((listing) => {
              const added = inEditor.has(String(listing.id));
              const isPicked = picked.some((l) => l.id === listing.id);
              return (
                <div key={listing.id} className="sw-library-item">
                  <ListingCard
                    listing={listing}
                    owned
                    picked={isPicked}
                    design={design}
                    note={added ? "In editor" : null}
                    label={added ? `Details of ${listingTitle(listing)}` : `${isPicked ? "Unselect" : "Select"} ${listingTitle(listing)}`}
                    onClick={() => toggle(listing)}
                  />
                  <button type="button" className="sw-link-button" onClick={() => onDetails(listing)}>
                    View details
                  </button>
                </div>
              );
            })}
            {!feed.loading && !feed.items.length && <p className="sw-empty">{feed.error || (query ? "No mockups match your search." : "No mockups in this folder yet.")}</p>}
            {feed.loading && <SkeletonCards count={feed.items.length ? 4 : 8} />}
            <div ref={sentinel} className="sw-sentinel" aria-hidden="true" />
          </div>
        </section>
      </div>
      <footer className="sw-library-footer">
        <span role="status">{notice || `${picked.length} selected · ${slots} editor slot${slots === 1 ? "" : "s"} available`}</span>
        <div>
          {picked.length > 0 && (
            <button type="button" className="sw-button" onClick={() => setPicked([])} disabled={busy}>
              Clear selection
            </button>
          )}
          <button type="button" className="sw-button sw-primary" onClick={add} disabled={!picked.length || busy}>
            {busy ? "Adding…" : picked.length ? `Add ${picked.length} mockup${picked.length === 1 ? "" : "s"}` : "Select mockups to add"}
          </button>
        </div>
      </footer>
    </Modal>
  );
}
